import { useCallback, useRef, useState } from 'react';

import { autocompletePlaces, getPlaceDetails } from '@/services/placesApi/placesApi';
import type { PlaceDetails, PlacePrediction } from '@/services/placesApi/placesApi.types';
import { debounce } from '@/utils/debounce';

import { createSessionToken } from './placesSession';

export const useAddressAutocomplete = () => {
  const sessionTokenRef = useRef(createSessionToken());
  const [placePredictions, setPlacePredictions] = useState<PlacePrediction[]>([]);
  const [activeInput, setActiveInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const runSearch = useCallback(
    debounce(async (input: string) => {
      try {
        const predictions = await autocompletePlaces({
          input,
          sessionToken: sessionTokenRef.current,
        });
        setPlacePredictions(predictions);
      } catch {
        setPlacePredictions([]);
      } finally {
        setIsLoading(false);
      }
    }, 400),
    [],
  );

  const searchPlaces = useCallback(
    (input: string) => {
      setActiveInput(input);
      setIsLoading(true);
      runSearch(input);
    },
    [runSearch],
  );

  const clearPlacePredictions = useCallback(() => {
    setPlacePredictions([]);
    setActiveInput('');
    setIsLoading(false);
  }, []);

  const fetchPlaceDetails = useCallback(async (placeId: string): Promise<PlaceDetails | null> => {
    try {
      const details = await getPlaceDetails(placeId, sessionTokenRef.current);
      sessionTokenRef.current = createSessionToken();
      return details;
    } catch {
      return null;
    }
  }, []);

  return {
    placePredictions: activeInput ? placePredictions : [],
    searchPlaces,
    clearPlacePredictions,
    fetchPlaceDetails,
    isLoading,
  };
};
